import { useEffect, useRef } from 'react';

/**
 * ConfirmationModal - Reusable confirmation dialog
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {function} props.onClose - Callback to close/cancel the modal
 * @param {function} props.onConfirm - Callback when user confirms
 * @param {string} props.title - Modal title
 * @param {string} props.message - Confirmation message
 * @param {string} props.confirmText - Text for confirm button (default: "Confirm")
 * @param {string} props.cancelText - Text for cancel button (default: "Cancel")
 * @param {string} props.confirmVariant - Button style variant: "danger" or "primary" (default: "primary")
 * @param {boolean} props.isProcessing - Whether action is processing (disables buttons, shows loading state)
 */
export default function ConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmVariant = 'primary',
  isProcessing = false,
}) {
  const confirmButtonRef = useRef(null);

  // Focus confirm button when modal opens (accessibility)
  useEffect(() => {
    if (isOpen && confirmButtonRef.current) {
      confirmButtonRef.current.focus();
    }
  }, [isOpen]);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !isProcessing) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, isProcessing, onClose]);

  if (!isOpen) {
    return null;
  }

  const confirmButtonClasses = confirmVariant === 'danger'
    ? 'px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-hidden focus:ring-2 focus:ring-red-500 disabled:bg-dark-600 disabled:cursor-not-allowed'
    : 'px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-blue-500 disabled:bg-dark-600 disabled:cursor-not-allowed';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={() => !isProcessing && onClose()}
    >
      {/*
        Note: role="dialog" and associated aria attributes intentionally applied
        to the modal content element (not the outer overlay). This keeps the
        overlay purely presentational while the dialog content is the interactive
        landmark for assistive technologies and testing queries. Tests rely on
        the dialog element (getByRole('dialog')) and remain valid.
      */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirmation-title"
        aria-describedby="confirmation-message"
        className="bg-dark-800 border border-dark-700 rounded-lg shadow-xl max-w-md w-full m-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-dark-700">
          <h2 id="confirmation-title" className="text-xl font-bold text-white">
            {title}
          </h2>
        </div>

        {/* Message */}
        <div className="px-6 py-4">
          <p id="confirmation-message" className="text-gray-300">
            {message}
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 px-6 py-4 bg-dark-700 rounded-b-lg">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-300 bg-dark-800 border border-dark-600 rounded-md hover:bg-dark-600 focus:outline-hidden focus:ring-2 focus:ring-gray-500"
            disabled={isProcessing}
          >
            {cancelText}
          </button>
          <button
            ref={confirmButtonRef}
            type="button"
            onClick={onConfirm}
            className={confirmButtonClasses}
            disabled={isProcessing}
          >
            {isProcessing ? 'Processing...' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
